import { appBaseUrl } from '@core/http/api-url.util';

import { LANDING_FAQ_ITEMS } from './landing-faq.constants';
import { buildOrganizationGraph, organizationId, websiteId } from './organization-structured-data';
import { SITE_NAME } from './seo.constants';

export type StructuredDataKind = 'landing' | 'about' | 'contact';

export function buildLandingStructuredData(): Record<string, unknown> {
  const base = appBaseUrl();
  const orgId = organizationId(base);
  const siteId = websiteId(base);

  return {
    '@context': 'https://schema.org',
    '@graph': [
      ...buildOrganizationGraph(base),
      {
        '@type': 'WebPage',
        '@id': `${base}/#webpage`,
        url: `${base}/`,
        name: `${SITE_NAME} | AI Mock Interview Practice for India`,
        description:
          'Voice AI mock interviews, ATS resume scoring, and JD-tailored rewrites for Indian job seekers. Practice in English or Hindi with STAR feedback.',
        isPartOf: { '@id': siteId },
        about: { '@id': orgId },
        inLanguage: 'en-IN',
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['#faqs summary', '#faqs .faq-answer'],
        },
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${base}/#software`,
        name: SITE_NAME,
        url: `${base}/`,
        applicationCategory: 'EducationalApplication',
        applicationSubCategory: 'Interview Preparation',
        operatingSystem: 'Web',
        inLanguage: ['English', 'Hindi'],
        description:
          'Akalan is an AI-powered mock interview and career prep platform for India. Practice voice interviews with dynamic follow-ups, ATS resume checks, and JD resume rewrites.',
        provider: { '@id': orgId },
        offers: [
          {
            '@type': 'Offer',
            name: 'Free tier',
            price: '0',
            priceCurrency: 'INR',
            description:
              'Limited free mock interviews, ATS resume checks, JD rewrites, and AI Coach chats. Current limits are shown on the pricing section.',
          },
          {
            '@type': 'Offer',
            name: 'Single interview',
            priceCurrency: 'INR',
            description: 'One additional AI mock interview with full feedback and report. See current price on the site.',
          },
          {
            '@type': 'Offer',
            name: 'Interview 10-pack',
            priceCurrency: 'INR',
            description: 'Ten AI mock interviews with unlimited AI coaching. See current price on the site.',
          },
        ],
        featureList: [
          'AI voice mock interviews with follow-up questions',
          'STAR format and filler-word feedback',
          'English and Hindi interview practice',
          'ATS resume analyzer with keyword gaps',
          'JD-tailored resume bullet rewrites',
          'Shareable interview performance reports',
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${base}/#faq`,
        mainEntity: LANDING_FAQ_ITEMS.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };
}

export { buildAboutStructuredData, buildContactStructuredData } from './organization-structured-data';
